import { TextStyleRenderer } from '../renderer/TextStyleRenderer';
import { TextAlignment } from '../../../../enums';
import { isNewline } from '../textMetricsHelpers';
import type { WordMetrics } from './WordMetrics';
import { TextLineMetrics } from './TextLineMetrics';
import {
    assignGloballyUniqueIndex,
    TextMetrics,
    positionLinesBelowEachOther,
    setAlignmentAndReadingDirection,
} from './TextMetrics';

type CanvasEvenlyLayoutWordMetricsSettings = {
    wordMetricsList: WordMetrics[];
    style: TextStyleRenderer;
    alignment: TextAlignment;
    wordBreak: boolean;
    maxWidth: number;
    maxHeight: number;
    removeTrailingPunctuationOnLastLineFromLastChunk: boolean;
};

/**
 * Arranges the words into lines, aiming to have the lines as evenly balanced or distributed in length as feasible. It's
 * possible for the last chunk to have one less line than the other chunks.
 */
export class CanvasEvenlyLayoutWordMetrics {
    #wordMetricsList: WordMetrics[];
    #style: TextStyleRenderer = new TextStyleRenderer();
    #alignment = TextAlignment.LEFT;
    #wordBreak = true;
    #maxWidth = 400;
    #maxHeight = 400;
    #removeTrailingPunctuationOnLastLineFromLastChunk = true;

    #lines: TextLineMetrics[] = [];
    #chunks: TextMetrics[] = [];

    private constructor(settings: CanvasEvenlyLayoutWordMetricsSettings) {
        this.#wordMetricsList = settings.wordMetricsList;
        this.#style = settings.style;
        this.#alignment = settings.alignment;
        this.#wordBreak = settings.wordBreak;
        this.#maxWidth = settings.maxWidth;
        this.#maxHeight = settings.maxHeight;
        this.#removeTrailingPunctuationOnLastLineFromLastChunk =
            settings.removeTrailingPunctuationOnLastLineFromLastChunk;
    }

    #run() {
        this.#createLines();
        this.#createChunks();

        if (this.#removeTrailingPunctuationOnLastLineFromLastChunk && this.chunks.length !== 0) {
            this.chunks.at(-1).removeTrailingPunctuations();
        }
    }

    get #baselineOffset() {
        return this.#style.baselineOffset;
    }

    #createLines() {
        this.#wordMetricsList.forEach((word) => word.clearCachedValues());

        if (this.#baselineOffset > this.#maxHeight) {
            console.log(
                `text it too tall to fit in the bounds: ${this.#baselineOffset} > ${this.#maxHeight}`,
            );
            return;
        }

        this.#createNewLine();
        this.#wordMetricsList.forEach((wordMetrics) => {
            if (isNewline(wordMetrics.text)) {
                this.#currentLine.add(wordMetrics);
                this.#createNewLine();
            } else {
                const futureLineWidth = wordMetrics.advanceWidth + this.#currentLine.advanceWidth;
                if (this.#wordBreak && futureLineWidth > this.#maxWidth) {
                    this.#createNewLine();
                }
                this.#currentLine.add(wordMetrics);
            }
        });
    }

    get #currentLine() {
        return this.#lines.at(-1);
    }

    #createNewLine() {
        const line = new TextLineMetrics();
        this.#lines.push(line);
        return line;
    }

    #maxLinesPerChunk() {
        return Math.floor(this.#maxHeight / this.#baselineOffset);
    }

    #linesPerChunk() {
        let maxLinesPerChunk = this.#maxLinesPerChunk();
        while (
            // evenly divide the lines into chunks
            this.#lines.length % maxLinesPerChunk !== 0 &&
            // one line difference is allowed
            maxLinesPerChunk - (this.#lines.length % maxLinesPerChunk) !== 1 &&
            // we should have at least 1 line
            maxLinesPerChunk > 1
        ) {
            maxLinesPerChunk--;
        }
        return maxLinesPerChunk;
    }

    #numberOfChunks() {
        return Math.ceil(this.#lines.length / this.#linesPerChunk());
    }

    #createChunks() {
        const numberOfChunks = this.#numberOfChunks();
        const linesPerChunk = this.#linesPerChunk();
        for (let i = 0; i < numberOfChunks; i++) {
            const lines = this.#lines.slice(i * linesPerChunk, (i + 1) * linesPerChunk);

            lines.forEach((line) => line.removeLeadingTrailingSpaces(this.#alignment));

            const lineWordMetrics = lines.flatMap((line) => line.words);
            assignGloballyUniqueIndex(lineWordMetrics);

            positionLinesBelowEachOther(lines, this.#style);
            setAlignmentAndReadingDirection(lines, this.#alignment);

            const textMetrics = new TextMetrics(lines);
            this.#chunks.push(textMetrics);
        }
    }

    get chunks() {
        return this.#chunks;
    }

    static layout({
        alignment = TextAlignment.LEFT,
        wordMetricsList = [],
        style,
        wordBreak = true,
        maxWidth = 400,
        maxHeight = 400,
        removeTrailingPunctuationOnLastLineFromLastChunk = true,
    }: {
        alignment?: TextAlignment;
        wordMetricsList?: WordMetrics[];
        style: TextStyleRenderer;
        wordBreak?: boolean;
        maxWidth?: number;
        maxHeight?: number;
        removeTrailingPunctuationOnLastLineFromLastChunk?: boolean;
    }) {
        const layout = new CanvasEvenlyLayoutWordMetrics({
            wordMetricsList,
            alignment,
            style,
            wordBreak,
            maxWidth,
            maxHeight,
            removeTrailingPunctuationOnLastLineFromLastChunk,
        });
        layout.#run();

        return layout.chunks;
    }
}
